import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_PARTNERS,
  GPX_ROUTES,
} from '@rsos/gainsight';
import { hashPhoneNumber } from '../utils/phoneHashUtils';

// Event names specific to Air Ambulance (currently GMR)
const AIR_AMBULANCE_EVENT_NAMES = {
  REQUEST_AIR_AMBULANCE: 'request_air_ambulance',
  OPEN_AIR_AMBULANCE_REQUEST_FORM: 'open_air_ambulance_request_form',
  CLOSE_AIR_AMBULANCE_REQUEST_FORM: 'close_air_ambulance_request_form',
  SUBMIT_AIR_AMBULANCE_REQUEST: 'submit_air_ambulance_request',
  SUBMIT_AIR_AMBULANCE_REQUEST_API_RESPONSE:
    'submit_air_ambulance_request_api_response',
  EDIT_AIR_AMBULANCE_REQUEST: 'edit_air_ambulance_request',
  CANCEL_AIR_AMBULANCE_REQUEST: 'cancel_air_ambulance_request',
  CANCEL_AIR_AMBULANCE_REQUEST_API_RESPONSE:
    'cancel_air_ambulance_request_api_response',
  OPEN_TRANSPORT_LINK: 'open_transport_link',
  COPY_TRACKING_LINK: 'copy_tracking_link',
  LOCATION_SELECTION: 'landing_location',
  SHOW_STATUS_WINDOW: 'show_air_ambulance_status',
  DISPLAY_STATUS_UPDATE: 'display_status_update',
  AIR_AMBULANCE_LAYER_CLICK_SHOW: 'show_air_ambulance_layer',
  AIR_AMBULANCE_LAYER_CLICK_HIDE: 'hide_air_ambulance_layer',
  // TODO: Not yet implemented
  LANDING_ZONE_CLICK: 'landing_zone_entity',
  LOCATION_CHANGE: 'change_location',
  FAQ_CLICK: 'open_faq_link',
  TOOLTIP_CLICK: 'open_form_help_tooltip',
};

// Enum for source button that opens the air ambulance request form
export const AIR_AMBULANCE_SOURCE_BUTTON = {
  ACTION_BAR: 'action_bar',
  OVERVIEW_TAB: 'overview_tab',
  TOOLS_TAB: 'tools_tab',
};

/**
 * Tracks when a user clicks the "Request Air Ambulance" button in ACTION BAR.
 * @param {Object} params - The parameters for tracking the request air ambulance click
 * @param {keyof typeof AIR_AMBULANCE_SOURCE_BUTTON} params.sourceButton - The source button that opened the air ambulance request form
 */
export const trackRequestAirAmbulanceClick = ({ sourceButton }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.REQUEST_AIR_AMBULANCE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      source_button: sourceButton,
    },
  });
};

/**
 * Tracks when a user opens the air ambulance request form.
 * @param {Object} params - The parameters for tracking the open air ambulance request form
 * @param {string} params.uuid - The UUID of the form open event
 */
export const trackOpenAirAmbulanceRequestForm = ({ uuid }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.OPEN_AIR_AMBULANCE_REQUEST_FORM,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      uuid,
    },
  });
};

/**
 * Tracks when a user closes the air ambulance request form.
 * @param {Object} params - The parameters for tracking the close air ambulance request form
 */
export const trackCloseAirAmbulanceRequestForm = () => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.CLOSE_AIR_AMBULANCE_REQUEST_FORM,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
  });
};

/**
 * Tracks when a user submits an air ambulance request.
 * @param {Object} params - The parameters for tracking the submit air ambulance request
 * @param {string} params.uuid - The UUID of the form open event
 * @param {string} params.requestId - The ID of the request being submitted
 * @param {string} params.requestType - The type of the request (e.g., immediate, standby)
 * @param {boolean} params.isEdit - Whether this is an edit of an existing request (true) or a new submission (false)
 */
export const trackSubmitAirAmbulanceRequest = ({
  uuid,
  requestId,
  requestType,
  isEdit = false,
}) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.SUBMIT_AIR_AMBULANCE_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      is_edit: isEdit,
    },
    entities: {
      request_id: hashPhoneNumber(requestId),
      request_type: requestType,
      uuid,
    },
  });
};

/**
 * Tracks when a user clicks the edit button for an existing air ambulance request.
 * @param {Object} params - The parameters for tracking the edit air ambulance request click
 * @param {string} params.requestId - The ID of the request being edited
 */
export const trackEditAirAmbulanceRequest = ({ requestId }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.EDIT_AIR_AMBULANCE_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
    },
  });
};

/**
 * Tracks when a user cancels an air ambulance request.
 * @param {Object} params - The parameters for tracking the cancel air ambulance request
 * @param {string} params.requestId - The ID of the request being cancelled
 * @param {string} params.reason - The reason for the cancellation
 */
export const trackCancelAirAmbulanceRequest = ({ requestId, reason }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.CANCEL_AIR_AMBULANCE_REQUEST,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      reason,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
    },
  });
};

/**
 * Tracks when a user clicks a Transport.net link.
 * @param {Object} params - The parameters for tracking the Transport.net link click
 * @param {string} params.airAmbulanceUrl - The URL of the Transport.net link
 * @param {string} params.requestId - The requestId associated with the request
 */
export const trackOpenTransportLink = ({ airAmbulanceUrl, requestId }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.OPEN_TRANSPORT_LINK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      air_ambulance_url: airAmbulanceUrl,
    },
  });
};

/**
 * Tracks when a user selects a location.
 * @param {Object} params - The parameters for tracking the location selection
 * @param {string} params.requestId - The requestId for the selected location
 * @param {Object} params.location - The location of the associated request
 * @param {('map_click'|'address_input'|'coordinates_input'|'call_location'|'existing_request')} [params.selectionMethod] - How the location was selected
 * @param {string} [params.uuid] - The form UUID to link with form open events
 *
 */
export const trackLocationSelection = ({
  requestId,
  location,
  selectionMethod,
  uuid,
}) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.LOCATION_SELECTION,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: selectionMethod
      ? { selection_method: selectionMethod }
      : undefined,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      location,
      ...(uuid && { uuid }),
    },
  });
};

/**
 * Tracks when a user copies a tracking link.
 * @param {Object} params - The parameters for tracking the copy tracking link
 * @param {string} params.requestId - The requestId associated with the request
 * @param {string} params.airAmbulanceUrl - The URL of the tracking link
 */
export const trackCopyTrackingLink = ({ requestId, airAmbulanceUrl }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.COPY_TRACKING_LINK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      air_ambulance_url: airAmbulanceUrl,
    },
  });
};

/**
 * Tracks when a user toggles the Air Ambulance layer visibility.
 * @param {Object} params - The parameters for tracking the air ambulance layer toggle
 * @param {string} params.layerId - The ID of the layer
 * @param {boolean} params.isVisible - Whether the layer is being shown (true) or hidden (false)
 */
export const trackAirAmbulanceLayerToggle = ({ layerId, isVisible }) => {
  gspx.trackEvent({
    name: isVisible
      ? AIR_AMBULANCE_EVENT_NAMES.AIR_AMBULANCE_LAYER_CLICK_SHOW
      : AIR_AMBULANCE_EVENT_NAMES.AIR_AMBULANCE_LAYER_CLICK_HIDE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      layer_id: layerId,
    },
  });
};

/**
 * Tracks when a status update is displayed.
 * @param {Object} params - The parameters for tracking the status update display
 * @param {string} params.requestId - The ID of the associated request
 * @param {string} params.status - The current status of the request

 */
export const trackDisplayStatusUpdate = ({ requestId, status }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.DISPLAY_STATUS_UPDATE,
    type: GPX_EVENT_TYPES.API_PUSH,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      status,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
    },
  });
};

/**
 * Tracks response from the API when a user submits an air ambulance request.
 * @param {Object} params - The parameters for tracking the submit air ambulance request
 * @param {string} params.requestId - The ID of the request being submitted
 * @param {number} params.status - The response status from the API
 * @param {string} params.error - The response error from the API
 * @param {boolean} params.success - The response success from the API
 * @param {string} params.UUID - The UUID of the form submission event
 * @param {boolean} params.isEdit - Whether this is an edit of an existing request (true) or a new submission (false)
 */
export const trackSubmitAirAmbulanceRequestApiResponse = ({
  requestId,
  status,
  error,
  success,
  UUID,
  isEdit = false,
}) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.SUBMIT_AIR_AMBULANCE_REQUEST_API_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      status,
      error,
      success,
      is_edit: isEdit,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      uuid: UUID,
    },
  });
};

/**
 * Tracks response from the API when a user cancels an air ambulance request.
 * @param {Object} params - The parameters for tracking the cancel air ambulance request
 * @param {string} params.requestId - The ID of the request being cancelled
 * @param {number} params.status - The response status from the API
 * @param {string} params.error - The response error from the API
 * @param {boolean} params.success - The response success from the API
 */
export const trackCancelAirAmbulanceRequestApiResponse = ({
  requestId,
  status,
  error,
  success,
  UUID,
}) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.CANCEL_AIR_AMBULANCE_REQUEST_API_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    additionalKeys: {
      status,
      error,
      success,
    },
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      uuid: UUID,
    },
  });
};

/**
 * Tracks when the AirAmbulanceStatus component is rendered.
 * @param {Object} params - The parameters for tracking the status page render
 * @param {string} params.requestId - The ID of the associated request
 * @param {string} params.status - The current state of the status page (e.g., SUBMITTED, ACCEPTED, CANCELLED)
 */
export const trackStatusPageRender = ({ requestId, status }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.SHOW_STATUS_WINDOW,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      status,
    },
    entities: {
      request_id: hashPhoneNumber(requestId),
    },
  });
};

// TODO: Not implemented yet
/**
 * Tracks when a user clicks on a landing zone.
 * @param {Object} params - The parameters for tracking the landing zone click
 * @param {string} params.requestId - The requestId for the selected location
 * @param {string} params.zoneId - The ID of the landing zone clicked
 */
export const trackLandingZoneClick = ({ zoneId, requestId }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.LANDING_ZONE_CLICK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      zone_id: zoneId,
    },
  });
};

/**
 * Tracks when a user changes the location.
 * @param {Object} params - The parameters for tracking the location change

 * @param {string} params.requestId - The ID of the associated request
 * @param {string} params.location - The location of the associated request
 */
export const trackLocationChange = ({ requestId, location }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.LOCATION_CHANGE,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
      location,
    },
  });
};

/**
 * Tracks when a user clicks on an FAQ link.
 * @param {Object} params - The parameters for tracking the FAQ click
 * @param {string} params.requestId - The requestId associated with the request
 */
export const trackFAQClick = ({ requestId }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.FAQ_CLICK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
    },
  });
};

/**
 * Tracks when a user opens a help tooltip.
 * @param {Object} params - The parameters for tracking the tooltip click
 * @param {string} params.requestId - The requestId associated with the request

 */
export const trackTooltipClick = ({ requestId }) => {
  gspx.trackEvent({
    name: AIR_AMBULANCE_EVENT_NAMES.TOOLTIP_CLICK,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GMR_AIR_AMBULANCE,
    capability: GPX_CAPABILITIES.AIR_AMBULANCE,
    partner: GPX_PARTNERS.GMR,
    route: GPX_ROUTES.MAP,
    entities: {
      request_id: hashPhoneNumber(requestId),
    },
  });
};
